import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import useAllOptions from 'data-hooks/useAllOptions';
import useFabricReviews from 'data-hooks/useFabricReviews';
import usePDPFabricFilters from 'data-hooks/usePDPFabricFilters';
import PDPShownInUI from './PDPShownInUI';
import usePDPOptions from '../state/context/usePDPOptions';

const PDPShownIn = ({
  fullWidth = false,
  onSelection,
  optionId,
  upholsteryName,
  valueId,
  productName = null,
  layout,
  showInactiveUpholstery = false,
}) => {
  const [showInactiveTooltip, setShowInactiveTooltip] = useState(false);

  const {
    actions: { onOptionValueSelection },
  } = usePDPOptions();

  const {
    data: { reviews, rating },
  } = useFabricReviews(valueId);

  const {
    data: { allMaterials },
  } = usePDPFabricFilters(null, false);

  const { allOptionsValues, allOptionsByValuesKey } = useAllOptions();

  const materialDetails = useMemo(() => {
    if (!allOptionsValues) {
      return null;
    }
    if (
      !valueId &&
      upholsteryName &&
      allOptionsByValuesKey &&
      showInactiveUpholstery
    ) {
      const valueKey = upholsteryName
        .toLowerCase()
        .trim()
        .split(' ')
        .join('-');
      return allOptionsByValuesKey[valueKey];
    }
    return allOptionsValues[valueId];
  }, [
    valueId,
    allOptionsValues,
    allOptionsByValuesKey,
    showInactiveUpholstery,
    upholsteryName,
  ]);

  // Only used to hide inactive tooltip on hover
  useEffect(() => {
    if (!allMaterials || !showInactiveUpholstery) {
      return;
    }
    const value = valueId
      ? allMaterials.find(({ materialId }) => materialId === valueId)
      : null;
    if (value && showInactiveTooltip) {
      setShowInactiveTooltip(false);
    }
  }, [valueId, allMaterials, showInactiveTooltip, showInactiveUpholstery]);

  if (!optionId || (!showInactiveUpholstery && !valueId) || !upholsteryName) {
    return null;
  }

  return (
    <PDPShownInUI
      upholsteryOptionTypeId={optionId}
      materialDetails={materialDetails}
      displayedUpholsteryLabel={upholsteryName}
      fullWidth={fullWidth}
      reviews={reviews}
      rating={rating}
      setUpholstery={() => {
        const value = valueId
          ? allMaterials.find(({ materialId }) => materialId === valueId)
          : null;
        if (showInactiveUpholstery && !value) {
          // Display tool tip instead
          setShowInactiveTooltip(true);
          return;
        }
        onOptionValueSelection(optionId, valueId);
        if (typeof onSelection === 'function') {
          onSelection();
        }
      }}
      productName={productName}
      layout={layout}
      showInactiveTooltip={showInactiveTooltip}
      showInactiveUpholstery={showInactiveUpholstery}
    />
  );
};

PDPShownIn.propTypes = {
  fullWidth: PropTypes.bool,
  layout: PropTypes.string,
  onSelection: PropTypes.func,
  optionId: PropTypes.number,
  productName: PropTypes.string,
  showInactiveUpholstery: PropTypes.bool,
  upholsteryName: PropTypes.string,
  valueId: PropTypes.number,
};

export default PDPShownIn;
